import React, {memo, useMemo} from 'react';
import styled from 'styled-components';
import {Interest, Palette, User} from 'hubbl-shared';
import {AvatarStorageRef} from '../avatar/AvatarStorageRef';
import {
  TextHeading5,
  TextHeading6,
  TextBodySmall,
  GradientPill,
} from '../index';
import {useAppSelector, useI18n} from '../../hooks';
import {CompanySelector} from '../../store/company/CompanySelector';
import {GlobalSelector} from '../../store/global/GlobalSelector';

interface EmployeeCardProps {
  employee: User;
  currentUserId?: string;
  showMatchingInterests?: boolean;
  matchingInterests?: Interest[];
  onClick?: (userId: string) => void;
  className?: string;
}

export const EmployeeCard = memo(
  ({
    employee,
    currentUserId,
    showMatchingInterests = false,
    matchingInterests = [],
    onClick,
    className,
  }: EmployeeCardProps) => {
    const isCurrentUser = employee.id === currentUserId;
    const isSSOEnabled = useAppSelector(
      CompanySelector.getCompanyDetails,
    )?.organizationalMetadata;
    const t = useI18n();

    const prefLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
    const handleClick = () => {
      if (onClick) {
        onClick(employee.id);
      }
    };

    return (
      <StyledEmployeeCard
        className={className}
        onClick={handleClick}
        title={t.events.clickToViewUserProfile}>
        <ClickHint>{t.events.viewProfile}</ClickHint>

        <CardHeader>
          <AvatarContainer>
            <AvatarStorageRef
              size={56}
              name={employee.fullName}
              imageRef={employee.photoUrl}
            />
          </AvatarContainer>
          <UserInfo>
            <UserName text={employee.fullName} />
            {employee.jobTitle && <UserTitle text={employee.jobTitle} />}
            <UserEmail text={employee.email} />
            {isCurrentUser && <YouTag>{t.activities.you}</YouTag>}
          </UserInfo>
        </CardHeader>

        <CardContent>
          <InfoRow>
            <InfoLabel text={t.events.department} />
            <InfoValue text={employee.department?.label?.en || 'N/A'} />
          </InfoRow>

          {isSSOEnabled && employee.customSSOProperties?.companyname && (
            <InfoRow>
              <InfoLabel text={t.events.company} />
              <InfoValue text={employee.customSSOProperties.companyname} />
            </InfoRow>
          )}

          {isSSOEnabled && employee.customSSOProperties?.officelocation && (
            <InfoRow>
              <InfoLabel text={t.events.officeLocation} />
              <InfoValue text={employee.customSSOProperties.officelocation} />
            </InfoRow>
          )}

          {employee.startedAtCompany && (
            <InfoRow>
              <InfoLabel text={t.events.started} />
              <InfoValue
                text={new Date(employee.startedAtCompany).toLocaleDateString()}
              />
            </InfoRow>
          )}
        </CardContent>

        {employee.interests && employee.interests.length > 0 && (
          <InterestsContainer>
            <TextHeading6 text={t.events.interests} />
            <div style={{marginTop: 8}}>
              {employee.interests.slice(0, 3).map((interest, index) => {
                return (
                  <GradientPill key={index}>
                    {interest.emoji} {interest.label[prefLanguage]}
                  </GradientPill>
                );
              })}
              {employee.interests.length > 3 && (
                <GradientPill>
                  +{employee.interests.length - 3} more
                </GradientPill>
              )}
            </div>
          </InterestsContainer>
        )}

        {!isCurrentUser &&
          showMatchingInterests &&
          matchingInterests.length > 0 && (
            <MatchingInterestsContainer>
              <TextHeading6 text={t.scenes.profile.matchingInterests} />
              <div style={{marginTop: 8}}>
                {matchingInterests.slice(0, 3).map((interest, index) => {
                  return (
                    <GradientPill key={index}>
                      {interest.emoji} {interest.label[prefLanguage]}
                    </GradientPill>
                  );
                })}
                {matchingInterests.length > 3 && (
                  <GradientPill>
                    +{matchingInterests.length - 3} {t.common.more}
                  </GradientPill>
                )}
              </div>
            </MatchingInterestsContainer>
          )}

        {employee.funFact && (
          <FunFactContainer>
            <FunFactText text={`"${employee.funFact}"`} />
          </FunFactContainer>
        )}
      </StyledEmployeeCard>
    );
  },
);

const StyledEmployeeCard = styled.div`
  background: ${Palette.white};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid ${Palette.almostBlackTransparent5};
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: ${Palette.primaryCherokee};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${Palette.primaryCherokee},
      ${Palette.primaryTerraCotta}
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const ClickHint = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${Palette.primaryTerraCotta};
  color: ${Palette.white};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;

  ${StyledEmployeeCard}:hover & {
    opacity: 1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const AvatarContainer = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 60px;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, ${Palette.primaryTerraCotta});
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  ${StyledEmployeeCard}:hover &::after {
    opacity: 1;
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
`;

const UserName = styled(TextHeading5)`
  margin: 0 0 4px 0;
  color: ${Palette.almostBlack};
  font-weight: 600;
  font-size: 18px;
  line-height: 1.3;
`;

const UserTitle = styled(TextBodySmall)`
  color: ${Palette.almostBlack750};
  font-weight: 500;
  margin-bottom: 2px;
`;

const UserEmail = styled(TextBodySmall)`
  color: ${Palette.almostBlack500};
  font-size: 12px;
`;

const YouTag = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background: ${Palette.primaryTerraCotta};
  color: ${Palette.white};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardContent = styled.div`
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled(TextBodySmall)`
  color: ${Palette.almostBlack750};
  font-weight: 500;
  font-size: 12px;
`;

const InfoValue = styled(TextBodySmall)`
  color: ${Palette.almostBlack};
  font-weight: 400;
  text-align: right;
  flex: 1;
  margin-left: 12px;
`;

const StatusBadge = styled.span<{color: string}>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: inline-block;
`;

const InterestsContainer = styled.div`
  margin-top: 16px;
`;

const MatchingInterestsContainer = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${Palette.primaryTerraCotta}20;
`;

const FunFactContainer = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${Palette.primarySubtleGreen};
  border-radius: 8px;
  border-left: 4px solid ${Palette.primaryCherokee};
`;

const FunFactText = styled(TextBodySmall)`
  font-style: italic;
  color: ${Palette.almostBlack750};
  line-height: 1.4;
`;
